import { DateTime } from "luxon";
import { z } from "zod";

import type { AthenaCoverageWriteConfig, AthenaCoverageWriteReviewInput } from "@/configuration/ehr/athena/types";
import { formatTprCasePolicyMemberId, resolveTprCasePolicyPackageId } from "@/configuration/groups/tpr/case-policies";
import { isTricareForLifeCoverage } from "@/configuration/groups/tpr/workflows/coverage-cob";
import {
  findUniqueTprInsurancePackageNumber,
  isTprUnitedHealthcarePayer,
  resolveTprEligibilityCustomerPayer,
  resolveTprMemberInsurancePackage,
} from "@/configuration/groups/tpr/workflows/insurance-grid";
import { normalizeInsuranceMemberId } from "@/packages/shared/src/models/insurance-evidence";
import payers from "@/src/data/payers.json";
import {
  eligibilityRequestMemberId,
  eligibilitySubscriberFieldsFromCoverage,
} from "@/src/lib/blocks/eligibility-check-inputs";
import { athenaStandardCoverageRank } from "@/src/lib/ehr/athena/athena-coverage-sequence";
import { resolveConfirmedAthenaInsurancePlan } from "@/src/lib/ehr/athena/athena-insurance-plan-catalog";
import { AthenaInsurancePackageResolutionSource } from "@/src/lib/ehr/athena/athena-insurance-resolution";
import { isSameDayEligibilityCheck } from "@/src/lib/insurance/eligibility-freshness";
import { CoverageType, type Coverage } from "@/src/models/coverage";

export function normalizeTprMemberIdForAthenaWrite(coverage: Coverage, memberId: string): string {
  if (isTprUnitedHealthcarePayer(coverage)) {
    const normalizedMemberId = memberId.replace(/[\s-]/g, "");

    return /^\d{11}$/.test(normalizedMemberId) ? normalizedMemberId.slice(0, 9) : memberId.replace(/-\d{2}$/, "");
  }

  if (coverage.customerPayer === "44273") {
    return memberId.replace(/^(HH[A-Z0-9]{7})-?\d{2}$/i, "$1");
  }

  const payerText = [coverage.payer, coverage.plan, coverage.customerPayer].filter(Boolean).join(" ");

  if (/\boscar\b/i.test(payerText)) {
    return memberId.replace(/-/g, "");
  }

  const casePolicyMemberId = formatTprCasePolicyMemberId(coverage, memberId);

  if (casePolicyMemberId !== null) return casePolicyMemberId;

  return /\bcigna\b/i.test(payerText) && /^\d{9}$/.test(memberId) ? `${memberId}01` : memberId;
}

export function tprWrittenMemberId(coverage: Coverage): string | null {
  return normalizeInsuranceMemberId(normalizeTprMemberIdForAthenaWrite(coverage, coverage.subscriberId?.trim() ?? ""));
}

const eligibilityPersonSchema = z.object({
  firstName: z.string().trim().min(1),
  lastName: z.string().trim().min(1),
  dateOfBirth: z.string().regex(/^\d{8}$/),
});
const eligibilityRequestSchema = z.object({
  tradingPartnerServiceId: z.string().min(1),
  // Dependent requests may omit an unknown subscriber DOB; the patient DOB remains
  // required on the dependent, and known subscriber/self DOBs are compared below.
  subscriber: eligibilityPersonSchema.extend({
    memberId: z.string().trim().min(1),
    dateOfBirth: eligibilityPersonSchema.shape.dateOfBirth.optional(),
  }),
  dependents: z.array(eligibilityPersonSchema).max(1).optional(),
  encounter: z.object({
    serviceTypeCodes: z.array(z.string()),
    dateOfService: z.string().optional(),
    beginningDateOfService: z.string().optional(),
    endDateOfService: z.string().optional(),
  }),
});

export function reviewTprCoverageWrite(input: AthenaCoverageWriteReviewInput): string | null {
  const { coverage, insurancePackageId, casePolicy, existing, eligibility, confirmedPlans, patient, group, now } =
    input;
  const writtenMemberId = tprWrittenMemberId(coverage);

  if (
    coverage.order !== 2 &&
    (isTricareForLifeCoverage(coverage) ||
      confirmedPlans.some(
        (plan) =>
          plan.packageId === insurancePackageId && isTricareForLifeCoverage({ payer: null, plan: plan.packageName })
      ))
  ) {
    return "Tricare for Life must be secondary. Confirm the primary coverage before continuing.";
  }

  const active = existing.filter((policy) => !policy.ehrCancelledAt);
  const matches = active.filter(
    (policy) =>
      policy.casePolicy === casePolicy &&
      policy.insurancePackageId === insurancePackageId &&
      normalizeInsuranceMemberId(policy.subscriberId) === writtenMemberId
  );

  const [match] = matches;
  const isSecondary = Number.isInteger(coverage.order) && (coverage.order ?? 0) > 1;
  const canReuseSecondary =
    isSecondary &&
    matches.length === 1 &&
    active.filter((policy) => policy.insurancePackageId === insurancePackageId).length === 1;

  if (matches.length === 1 && match && (casePolicy || athenaStandardCoverageRank(existing, match) === coverage.order)) {
    return null;
  }

  const cancelledMatches = existing.filter(
    (policy) =>
      policy.ehrCancelledAt &&
      policy.insurancePackageId === insurancePackageId &&
      normalizeInsuranceMemberId(policy.subscriberId) === writtenMemberId
  );

  // Only primary medical coverage can be reverified electronically. Athena exposes
  // cancellation dates without times, so a check on that same day cannot establish
  // that eligibility was verified after the cancellation.
  if (
    cancelledMatches.length > 0 &&
    (casePolicy ||
      coverage.order !== 1 ||
      cancelledMatches.some((policy) => {
        const cancelledAt = DateTime.fromFormat(policy.ehrCancelledAt ?? "", "MM/dd/yyyy", { zone: group.timezone });
        const checkedDay = DateTime.fromISO(eligibility?.createdAt ?? "", { zone: group.timezone }).startOf("day");

        return !cancelledAt.isValid || !checkedDay.isValid || cancelledAt >= checkedDay;
      }))
  ) {
    return "This member's policy was previously cancelled in Athena. Review that correction before adding it again, regardless of who cancelled it.";
  }

  // Case policies are outside standard coverage ordering and remain untouched by the cascade.
  // Corrections to standard package/member identity or case-policy writes still need review.
  if (
    active.some(
      (policy) =>
        (casePolicy && policy.casePolicy) || (policy.insurancePackageId === insurancePackageId && !canReuseSecondary)
    )
  ) {
    return "The proposed insurance would correct an existing Athena package or change case-policy ordering. Verify the chart manually before continuing.";
  }

  if (casePolicy) {
    if (
      coverage.coverageType !== CoverageType.INSURANCE ||
      !writtenMemberId ||
      resolveTprCasePolicyPackageId(coverage)?.toString() !== insurancePackageId
    ) {
      return "A configured case-policy package and member ID are required before adding insurance.";
    }

    return null;
  }

  if (coverage.coverageType !== CoverageType.INSURANCE) {
    return coverage.coverageType === CoverageType.SELF_PAY &&
      coverage.order === 1 &&
      insurancePackageId === "0" &&
      active.length === 0
      ? null
      : "Self Pay requires primary coverage with no active Athena policies. Verify the chart manually before continuing.";
  }

  if (isSecondary) {
    if (!writtenMemberId) {
      return "A member ID is required before adding secondary insurance.";
    }

    if (matches.length === 1) return null;
    // Secondary package identity can come from its own extraction and verified catalog.
    // This does not assert eligibility and cannot authorize a primary-slot write.
    if (coverage.order === 2 && input.resolutionSource === AthenaInsurancePackageResolutionSource.AGENT_EXTRACTION)
      return null;

    // The referral eligibility result belongs to the primary. Resolve the
    // secondary from its own identity without borrowing that result.
    const catalogPackageId = resolveConfirmedAthenaInsurancePlan(confirmedPlans, coverage, null, null, null)?.packageId;
    const memberPackageId = resolveTprMemberInsurancePackage({
      subscriberId: coverage.subscriberId,
      payer: coverage.payer,
      plan: coverage.plan,
      eligibilityPayerName: null,
      eligibilityProductText: null,
    });
    const packageId =
      memberPackageId ??
      (coverage.valerieInsurancePlanId
        ? catalogPackageId
        : (findUniqueTprInsurancePackageNumber({
            payer: coverage.payer,
            plan: coverage.plan,
            groupNumber: coverage.groupNumber,
            eligibilityProductText: null,
          }) ?? catalogPackageId));

    return packageId === insurancePackageId
      ? null
      : "The secondary insurance needs a confirmed Athena package match. Confirm the specific plan and network before adding coverage.";
  }

  if (coverage.order !== 1) {
    return "Confirm the insurance order before adding coverage.";
  }

  const eligibilityProductText = [eligibility?.planType, eligibility?.planName].filter(Boolean).join(" ");
  const catalogPackageId = resolveConfirmedAthenaInsurancePlan(
    confirmedPlans,
    coverage,
    eligibility?.payerName ?? null,
    eligibilityProductText,
    eligibility?.planName ?? null,
    input.candidatePlanIds
  )?.packageId;

  // Ops verifies Gold Kidney eligibility with the payer when electronic verification is unavailable.
  if (
    cancelledMatches.length === 0 &&
    coverage.customerPayer === "A6865" &&
    writtenMemberId &&
    catalogPackageId === insurancePackageId &&
    (!eligibility ||
      (!eligibility.deletedAt &&
        eligibility.patientId === patient.id &&
        eligibility.groupId === group.id &&
        eligibility.eligibilityStatus !== "active" &&
        eligibility.eligibilityStatus !== "inactive"))
  ) {
    return null;
  }

  const currentTime = DateTime.fromISO(now, { zone: group.timezone });

  if (
    !eligibility ||
    eligibility.deletedAt ||
    eligibility.patientId !== patient.id ||
    eligibility.groupId !== group.id ||
    eligibility.checkFailed !== false ||
    eligibility.eligibilityStatus !== "active" ||
    !isSameDayEligibilityCheck(eligibility.createdAt, group.timezone, now)
  ) {
    return "A current, active eligibility check for this patient is required before adding insurance. Verify coverage manually if electronic eligibility is unavailable.";
  }

  const request = eligibilityRequestSchema.safeParse(eligibility.requestJson);
  const fields = eligibilitySubscriberFieldsFromCoverage(coverage, patient);
  const customerPayer = resolveTprEligibilityCustomerPayer(coverage);
  const payerId = payers.find((payer) => payer.primaryPayerId === customerPayer)?.primaryPayerId;
  const subscriberDob = fields.checkAsDependent ? fields.subscriberDateOfBirth : fields.dateOfBirth;
  const today = currentTime.toFormat("yyyyMMdd");

  if (
    !request.success ||
    !payerId ||
    !fields.dateOfBirth ||
    request.data.tradingPartnerServiceId !== payerId ||
    request.data.subscriber.memberId !== eligibilityRequestMemberId(payerId, coverage.subscriberId?.trim() ?? "") ||
    request.data.subscriber.firstName !== fields.subscriberGivenName?.trim() ||
    request.data.subscriber.lastName !== fields.subscriberFamilyName?.trim() ||
    request.data.subscriber.dateOfBirth !== subscriberDob?.replaceAll("-", "") ||
    !request.data.encounter.serviceTypeCodes.includes("30") ||
    [
      request.data.encounter.dateOfService,
      request.data.encounter.beginningDateOfService,
      request.data.encounter.endDateOfService,
    ].some((date) => date !== undefined && date !== today) ||
    (fields.checkAsDependent
      ? request.data.dependents?.[0]?.firstName !== patient.givenName ||
        request.data.dependents?.[0]?.lastName !== patient.familyName ||
        request.data.dependents?.[0]?.dateOfBirth !== patient.dateOfBirth?.replaceAll("-", "")
      : Boolean(request.data.dependents?.length))
  ) {
    return "The eligibility request does not match the coverage being added. Recheck the correct member and payer or verify coverage manually.";
  }

  // The agent validates package identity against the catalog/live directory. Keep all
  // patient, eligibility, order and existing-policy guards above, including fresh
  // verification after any historical cancellation.
  if (input.resolutionSource === AthenaInsurancePackageResolutionSource.AGENT) return null;

  const memberPackageId = resolveTprMemberInsurancePackage({
    subscriberId: coverage.subscriberId,
    payer: coverage.payer,
    plan: coverage.plan,
    eligibilityPayerName: eligibility.payerName,
    eligibilityProductText,
  });
  const packageId =
    memberPackageId ??
    (coverage.valerieInsurancePlanId || input.candidatePlanIds?.length
      ? catalogPackageId
      : (findUniqueTprInsurancePackageNumber({
          payer: coverage.payer,
          plan: coverage.plan,
          groupNumber: coverage.groupNumber,
          eligibilityProductText,
        }) ?? catalogPackageId));

  return packageId === insurancePackageId
    ? null
    : "Eligibility does not establish this Athena package match. Confirm the specific plan and network manually before adding coverage.";
}

export const tprAthenaCoverageWrite = {
  review: reviewTprCoverageWrite,
  memberId: normalizeTprMemberIdForAthenaWrite,
} satisfies AthenaCoverageWriteConfig;
